import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Alert, Button, Card, SelectField } from "../../../components/ui";
import { ConfirmDialog } from "../../../components/ui/overlay/ConfirmDialog";
import { AppShell } from "../../../layouts/AppShell";
import {
  getQuestionnaire,
  listQuestionnaireVersions,
} from "../../questionnaire/api/questionnaireAdminApi";
import { downloadMemberQuestionnaireCsv } from "../api/dataExportApi";

export function DataExportPage() {
  const [versionId, setVersionId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string>();
  const versions = useQuery({
    queryKey: ["questionnaire", "versions"],
    queryFn: listQuestionnaireVersions,
  });
  const current = useQuery({
    queryKey: ["questionnaire"],
    queryFn: getQuestionnaire,
    retry: false,
  });
  const orderedVersions = useMemo(
    () =>
      [...(versions.data ?? [])].sort(
        (left, right) => right.versionNumber - left.versionNumber,
      ),
    [versions.data],
  );

  const selectedVersionId =
    versionId ||
    orderedVersions.find((version) => version.id === current.data?.versionId)
      ?.id ||
    orderedVersions[0]?.id ||
    "";

  const download = async () => {
    if (!selectedVersionId || downloading) return;
    setDownloading(true);
    setError(undefined);
    try {
      const result = await downloadMemberQuestionnaireCsv(selectedVersionId);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setConfirming(false);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "資料匯出失敗，請稍後再試。",
      );
    } finally {
      setDownloading(false);
    }
  };

  const noVersions = !versions.isLoading && orderedVersions.length === 0;
  return (
    <AppShell title="資料匯出" breadcrumb={["管理後台", "資料匯出"]}>
      <Alert variant="warning">
        匯出檔包含會員聯絡與企業資料，以及具名問卷回答。請僅供核准用途使用，妥善保管並避免轉傳。
      </Alert>
      {error && <Alert variant="danger">{error}</Alert>}
      <Card title="會員與問卷資料">
        <p>
          CSV
          將包含所有學生會員（含停用帳號）；未填寫所選問卷版本者仍會列出，回答欄位留白。
        </p>
        {versions.isError && <Alert variant="danger">無法載入問卷版本。</Alert>}
        {noVersions && (
          <Alert variant="info">
            目前沒有已發布的問卷版本，無法產生合併匯出檔。
          </Alert>
        )}
        <SelectField
          label="問卷版本"
          value={selectedVersionId}
          disabled={versions.isLoading || noVersions}
          onChange={(event) => setVersionId(event.target.value)}
        >
          {orderedVersions.map((version) => (
            <option key={version.id} value={version.id}>
              V{version.versionNumber} - {version.title}
              {version.id === current.data?.versionId ? "（目前版本）" : ""}
            </option>
          ))}
        </SelectField>
        <Button
          disabled={!selectedVersionId || versions.isLoading || noVersions}
          onClick={() => setConfirming(true)}
        >
          下載 CSV
        </Button>
      </Card>
      <ConfirmDialog
        open={confirming}
        title="確認下載具名資料"
        description="此操作會留下稽核紀錄。下載後請依隱私與資料保存規範妥善管理檔案。"
        confirmLabel="確認下載"
        loading={downloading}
        onConfirm={() => void download()}
        onClose={() => !downloading && setConfirming(false)}
      />
    </AppShell>
  );
}
